import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  MenuItem,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Alert,
  Box,
  Chip,
  FormControlLabel,
  Switch,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Stack
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  CarRental as CarIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { Device, DeviceTypeOptions } from '../types';
import { deviceAPI, vehicleAPI } from '../services/api';

interface AddDeviceDialogProps {
  open: boolean;
  onClose: () => void;
  onAdded?: (device: Device) => void;
  mode?: 'add' | 'bind' | 'rebind';
  existingDevice?: Device;
}

const steps = ['Device Info', 'Vehicle Details', 'Security Setup'];

const AddDeviceDialog: React.FC<AddDeviceDialogProps> = ({
  open,
  onClose,
  onAdded,
  mode = 'add',
  existingDevice
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Device form state
  const [deviceData, setDeviceData] = useState({
    imei: existingDevice?.imei || '',
    plateNumber: existingDevice?.plateNumber || '',
    make: existingDevice?.make || '',
    model: existingDevice?.model || '',
    modelYear: existingDevice?.modelYear || '',
    deviceType: (existingDevice?.deviceType || 'My Car Pro') as DeviceTypeOptions,
    vin: existingDevice?.vin || '',
    // Health monitoring settings
    healthMonitoring: true,
    alertsEnabled: true,
    autoBlockOnSwap: true,
    maxSignalLoss: 10,
  });

  // VIN auto-fetch states
  const [vinFetching, setVinFetching] = useState(false);
  const [vinAutoFetched, setVinAutoFetched] = useState(false);
  const [vinSource, setVinSource] = useState<string>('');

  // Vehicle workflow states
  const [addVehicleNow, setAddVehicleNow] = useState<boolean | null>(null);
  const [vehicleFormData, setVehicleFormData] = useState({
    plateNumber: '',
    category: 'general' as 'executive' | 'staff_bus' | 'technical' | 'general',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    fuelType: 'petrol' as 'petrol' | 'diesel' | 'electric' | 'hybrid',
  });

  const [validationErrors, setValidationErrors] = useState({
    imei: '',
    vin: '',
    vehicle: ''
  });

  // Auto-fetch VIN when IMEI is entered
  const handleVINAutoFetch = async (imei: string) => {
    if (imei.length !== 15) return;

    setVinFetching(true);
    setVinAutoFetched(false);
    setVinSource('');

    try {
      const result = await deviceAPI.fetchVINFromIMEI(imei);

      if (result.found && result.vin) {
        setDeviceData(prev => ({ ...prev, vin: result.vin || '' }));
        setVinAutoFetched(true);
        setVinSource(result.source || 'Unknown');

        // Pre-populate vehicle form if VIN found
        setVehicleFormData(prev => ({
          ...prev,
          make: deviceData.make || prev.make,
          model: deviceData.model || prev.model,
          year: parseInt(deviceData.modelYear) || prev.year
        }));
      }
    } catch (error) {
      console.error('VIN auto-fetch failed:', error);
    } finally {
      setVinFetching(false);
    }
  };

  const validateIMEI = async (imei: string) => {
    if (!/^\d{15}$/.test(imei)) {
      setValidationErrors(prev => ({ ...prev, imei: 'IMEI must be exactly 15 digits' }));
      return false;
    }

    try {
      const check = await deviceAPI.checkImeiAvailability(imei);
      if (!check.available && mode === 'add') {
        setValidationErrors(prev => ({ ...prev, imei: 'This IMEI already exists' }));
        return false;
      }
      setValidationErrors(prev => ({ ...prev, imei: '' }));
      return true;
    } catch (error) {
      setValidationErrors(prev => ({ ...prev, imei: 'Failed to validate IMEI' }));
      return false;
    }
  };

  const validateVIN = (vin: string) => {
    if (vin && !/^[A-HJ-NPR-Z0-9]{17}$/.test(vin)) {
      setValidationErrors(prev => ({ ...prev, vin: 'Invalid VIN format (17 characters)' }));
      return false;
    }
    setValidationErrors(prev => ({ ...prev, vin: '' }));
    return true;
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      // Step 1: Device Info - Only IMEI and VIN validation
      const imeiValid = await validateIMEI(deviceData.imei);
      const vinValid = validateVIN(deviceData.vin);

      if (!imeiValid || !vinValid) {
        return;
      }

      // Clear any previous validation errors for vehicle fields
      setValidationErrors(prev => ({ ...prev, vehicle: '' }));
    }

    if (activeStep === 1) {
      // Step 2: Vehicle Details - Only validate if user chose to add vehicle NOW
      if (addVehicleNow === true) {
        // Validate required vehicle fields
        if (!vehicleFormData.plateNumber || !vehicleFormData.plateNumber.trim()) {
          setValidationErrors(prev => ({ ...prev, vehicle: 'Plate number is required' }));
          return;
        }

        if (!vehicleFormData.make || !vehicleFormData.make.trim()) {
          setValidationErrors(prev => ({ ...prev, vehicle: 'Make is required' }));
          return;
        }

        if (!vehicleFormData.model || !vehicleFormData.model.trim()) {
          setValidationErrors(prev => ({ ...prev, vehicle: 'Model is required' }));
          return;
        }

        // Clear validation errors if all fields are valid
        setValidationErrors(prev => ({ ...prev, vehicle: '' }));
      } else if (addVehicleNow === false) {
        // User chose to skip - no validation needed
        setValidationErrors(prev => ({ ...prev, vehicle: '' }));
      } else {
        // User hasn't chosen yet - don't proceed
        return;
      }
    }

    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      let device: Device;

      // Step 1: Create device with ALL data (even if empty)
      if (mode === 'add') {
        device = await deviceAPI.createDevice({
          imei: deviceData.imei,
          plateNumber: deviceData.plateNumber || '', // Can be empty
          make: deviceData.make || '', // Can be empty
          model: deviceData.model || '', // Can be empty
          modelYear: deviceData.modelYear || '', // Can be empty
          deviceType: deviceData.deviceType,
          vin: deviceData.vin || '', // VIN from auto-fetch (can be empty)
        });

        console.log('✅ Device created:', device);
      } else {
        device = existingDevice!;
      }

      // Step 2: Create vehicle if user chose to add now AND we have VIN
      if (addVehicleNow && deviceData.vin) {
        console.log('📝 Creating vehicle for device:', device._id);

        await vehicleAPI.quickAddFromDevice({
          deviceId: device._id!,
          vin: deviceData.vin,
          plateNumber: vehicleFormData.plateNumber,
          category: vehicleFormData.category,
          make: vehicleFormData.make || deviceData.make || 'Unknown',
          model: vehicleFormData.model || deviceData.model || 'Unknown',
          year: vehicleFormData.year,
          color: vehicleFormData.color,
          fuelType: vehicleFormData.fuelType,
        });

        console.log('✅ Vehicle created and bound');
      }

      // Success - show appropriate message
      const successMessage = addVehicleNow
        ? 'Device and vehicle created successfully!'
        : deviceData.vin
          ? 'Device created with VIN. You can add vehicle details later from Fleet Management.'
          : 'Device created successfully. VIN will be fetched when device sends data.';

      alert(successMessage);

      onAdded?.(device);
      onClose();
      resetForm();

    } catch (error: any) {
      console.error('Failed to create device/vehicle:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to save';
      alert(`Error: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setDeviceData({
      imei: '',
      plateNumber: '',
      make: '',
      model: '',
      modelYear: '',
      deviceType: 'My Car Pro',
      vin: '',
      healthMonitoring: true,
      alertsEnabled: true,
      autoBlockOnSwap: true,
      maxSignalLoss: 10,
    });
    setVehicleFormData({
      plateNumber: '',
      category: 'general',
      make: '',
      model: '',
      year: new Date().getFullYear(),
      color: '',
      fuelType: 'petrol',
    });
    setAddVehicleNow(null);
    setVinAutoFetched(false);
    setVinSource('');
    setActiveStep(0);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="IMEI"
                  value={deviceData.imei}
                  onChange={(e) => {
                    const digitsOnly = e.target.value.replace(/[^0-9]/g, '');
                    setDeviceData(prev => ({ ...prev, imei: digitsOnly }));
                  }}
                  onBlur={() => {
                    validateIMEI(deviceData.imei);
                    handleVINAutoFetch(deviceData.imei);
                  }}
                  error={!!validationErrors.imei}
                  helperText={validationErrors.imei || 'Enter 15-digit IMEI - VIN will auto-fetch'}
                  inputProps={{ maxLength: 15 }}
                  disabled={mode !== 'add' || vinFetching}
                  InputProps={{
                    endAdornment: vinFetching ? (
                      <CircularProgress size={20} />
                    ) : vinAutoFetched ? (
                      <CheckCircle color="success" />
                    ) : null
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="VIN"
                  value={deviceData.vin}
                  onChange={(e) => {
                    const formatted = e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
                    setDeviceData(prev => ({ ...prev, vin: formatted }));
                    setVinAutoFetched(false);
                  }}
                  onBlur={() => validateVIN(deviceData.vin)}
                  error={!!validationErrors.vin}
                  helperText={
                    vinAutoFetched
                      ? `VIN auto-fetched `
                      : validationErrors.vin || 'VIN will be fetched automatically from telemetry'
                  }
                  inputProps={{ maxLength: 17 }}
                  disabled={true}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderColor: vinAutoFetched ? 'success.main' : undefined,
                      bgcolor: vinAutoFetched ? 'success.lighter' : 'grey.50'
                    }
                  }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: vinFetching ? (
                      <CircularProgress size={20} />
                    ) : vinAutoFetched ? (
                      <CheckCircle color="success" />
                    ) : null
                  }}
                />
                {vinAutoFetched && (
                  <Chip
                    size="small"
                    icon={<CheckCircle />}
                    label={`Auto-fetched `}
                    color="success"
                    sx={{ mt: 1 }}
                  />
                )}
                {!vinAutoFetched && !vinFetching && deviceData.imei.length === 15 && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="caption">
                      No VIN found in telemetry for this device. The device may be new or hasn't sent VIN data yet.
                    </Typography>
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12} md={12}>
                <TextField
                  select
                  fullWidth
                  label="Device Type"
                  value={deviceData.deviceType}
                  onChange={(e) => setDeviceData(prev => ({
                    ...prev,
                    deviceType: e.target.value as DeviceTypeOptions
                  }))}
                >
                  <MenuItem value="My Car Pro">My Car Pro</MenuItem>
                  <MenuItem value="My Car Lite">My Car Lite</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            {addVehicleNow === null && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom fontWeight="bold">
                  Would you like to add vehicle details now?
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {deviceData.vin
                    ? `Device has VIN: ${deviceData.vin}. You can create the full vehicle profile now or add it later from Fleet Management.`
                    : 'You can add complete vehicle information now or save just the device and add vehicle details later from Fleet Management.'
                  }
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    startIcon={<CarIcon />}
                    onClick={() => setAddVehicleNow(true)}
                  >
                    Add Vehicle Details Now
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setAddVehicleNow(false)}
                  >
                    Skip for Now
                  </Button>
                </Stack>
              </Alert>
            )}

            {addVehicleNow && (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    ✓ Creating vehicle with VIN: <strong>{deviceData.vin}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Device will be automatically bound to this vehicle.
                  </Typography>
                </Alert>

                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CarIcon />
                  Vehicle Information
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      disabled
                      label="VIN"
                      value={deviceData.vin}
                      helperText="VIN from device (auto-fetched)"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Plate Number"
                      value={vehicleFormData.plateNumber}
                      onChange={(e) => setVehicleFormData(prev => ({
                        ...prev,
                        plateNumber: e.target.value.toUpperCase()
                      }))}
                      error={!!validationErrors.vehicle}
                      helperText={validationErrors.vehicle}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={vehicleFormData.category}
                        onChange={(e) => setVehicleFormData(prev => ({
                          ...prev,
                          category: e.target.value as any
                        }))}
                        label="Category"
                      >
                        <MenuItem value="executive">Executive Pool</MenuItem>
                        <MenuItem value="staff_bus">Staff Bus</MenuItem>
                        <MenuItem value="technical">Technical Units</MenuItem>
                        <MenuItem value="general">General Pool</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      label="Make"
                      value={vehicleFormData.make}
                      onChange={(e) => setVehicleFormData(prev => ({
                        ...prev,
                        make: e.target.value
                      }))}
                      placeholder={deviceData.make || 'e.g. Toyota'}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      label="Model"
                      value={vehicleFormData.model}
                      onChange={(e) => setVehicleFormData(prev => ({
                        ...prev,
                        model: e.target.value
                      }))}
                      placeholder={deviceData.model || 'e.g. Camry'}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label="Year"
                      value={vehicleFormData.year}
                      onChange={(e) => setVehicleFormData(prev => ({
                        ...prev,
                        year: parseInt(e.target.value)
                      }))}
                      inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Color"
                      value={vehicleFormData.color}
                      onChange={(e) => setVehicleFormData(prev => ({
                        ...prev,
                        color: e.target.value
                      }))}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Fuel Type</InputLabel>
                      <Select
                        value={vehicleFormData.fuelType}
                        onChange={(e) => setVehicleFormData(prev => ({
                          ...prev,
                          fuelType: e.target.value as any
                        }))}
                        label="Fuel Type"
                      >
                        <MenuItem value="petrol">Petrol</MenuItem>
                        <MenuItem value="diesel">Diesel</MenuItem>
                        <MenuItem value="electric">Electric</MenuItem>
                        <MenuItem value="hybrid">Hybrid</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </>
            )}

            {addVehicleNow === false && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  Device will be saved without vehicle details
                </Typography>
                <Typography variant="body2">
                  You can add vehicle information later from:
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                  • Fleet Management → Vehicles → Add Vehicle
                </Typography>
              </Alert>
            )}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon />
              Security & Monitoring Settings
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={deviceData.healthMonitoring}
                      onChange={(e) => setDeviceData(prev => ({
                        ...prev,
                        healthMonitoring: e.target.checked
                      }))}
                    />
                  }
                  label="Enable Health Monitoring"
                />
                <Typography variant="body2" color="text.secondary">
                  Monitor device connectivity, battery, and diagnostic information
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={deviceData.alertsEnabled}
                      onChange={(e) => setDeviceData(prev => ({
                        ...prev,
                        alertsEnabled: e.target.checked
                      }))}
                    />
                  }
                  label="Enable Security Alerts"
                />
                <Typography variant="body2" color="text.secondary">
                  Receive alerts for speeding, unauthorized access, and device issues
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={deviceData.autoBlockOnSwap}
                      onChange={(e) => setDeviceData(prev => ({
                        ...prev,
                        autoBlockOnSwap: e.target.checked
                      }))}
                    />
                  }
                  label="Auto-block on Unauthorized Swap"
                />
                <Typography variant="body2" color="text.secondary">
                  Automatically block device if unauthorized vehicle binding is detected
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Signal Loss Time (minutes)"
                  value={deviceData.maxSignalLoss}
                  onChange={(e) => setDeviceData(prev => ({
                    ...prev,
                    maxSignalLoss: parseInt(e.target.value)
                  }))}
                  inputProps={{ min: 1, max: 60 }}
                  helperText="Device will be marked as disconnected after this time"
                />
              </Grid>
            </Grid>

            {/* Summary */}
            <Box sx={{
              mt: 3,
              p: 2,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1
            }}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="primary">
                Setup Summary:
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2">
                  • Device: {deviceData.deviceType} (IMEI: {deviceData.imei})
                </Typography>
                {deviceData.vin && (
                  <Typography variant="body2" sx={{ color: vinAutoFetched ? 'success.main' : 'text.primary' }}>
                    • VIN: {deviceData.vin} {vinAutoFetched && '(Auto-fetched ✓)'}
                  </Typography>
                )}
                {addVehicleNow && vehicleFormData.plateNumber && (
                  <Typography variant="body2" color="success.main">
                    • Vehicle: {vehicleFormData.make || 'Unknown'} {vehicleFormData.model || 'Unknown'} - {vehicleFormData.plateNumber}
                  </Typography>
                )}
                {addVehicleNow === false && (
                  <Typography variant="body2" color="text.secondary">
                    • Vehicle: Will be added later
                  </Typography>
                )}
                <Typography variant="body2">
                  • Health Monitoring: {deviceData.healthMonitoring ? '✓ Enabled' : '✗ Disabled'}
                </Typography>
                <Typography variant="body2">
                  • Security Alerts: {deviceData.alertsEnabled ? '✓ Enabled' : '✗ Disabled'}
                </Typography>
                <Typography variant="body2">
                  • Auto-block on Swap: {deviceData.autoBlockOnSwap ? '✓ Enabled' : '✗ Disabled'}
                </Typography>
              </Stack>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        {mode === 'add' ? 'Add New Device' : mode === 'bind' ? 'Bind Device to Vehicle' : 'Rebind Device'}
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 2 }}>
          {steps.map((label, index) => (
            <Step key={label} completed={activeStep > index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading || vinFetching}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleComplete}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Processing...' : (
              addVehicleNow ? 'Create Device & Vehicle' : 'Create Device'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddDeviceDialog;